/**
 * ============================================================================
 * CREATE TRANSACTION COMPONENT - Dashboard-wt styling
 * ============================================================================
 * Quick transaction setup; rendered inside Create Transaction page (dashboard-wt layout).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransaction } from '../../hooks';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Input from '../common/Input';
import Textarea from '../common/Textarea';
import Select from '../common/Select';

const CreateTransaction = () => {
  const navigate = useNavigate();
  const { createTransaction, loading } = useTransaction();
  const { showSuccess, showError } = useToast();
  const { formatCurrency, language } = useI18n();

  const [step, setStep] = useState(1);
  const totalSteps = 3;

  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    currency: 'XAF',
    deliveryDays: '',
    description: '',
  });

  const [errors, setErrors] = useState({});

  const amount = parseFloat(formData.amount) || 0;
  const fee = Math.min(amount * 0.015, 15000);
  const totalAmount = amount + fee;

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = (stepToValidate = step) => {
    const newErrors = {};
    if (stepToValidate >= 1) {
      if (!formData.title.trim()) {
        newErrors.title = language === 'fr' ? 'Le titre est requis' : 'Title is required';
      }
    }
    if (stepToValidate >= 2) {
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        newErrors.amount = language === 'fr' ? 'Le montant doit être supérieur à 0' : 'Amount must be greater than 0';
      }
      if (!formData.deliveryDays || parseInt(formData.deliveryDays) <= 0) {
        newErrors.deliveryDays = language === 'fr' ? 'Le délai de livraison est requis' : 'Delivery time is required';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate(step)) setStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const deliveryDays = parseInt(formData.deliveryDays);
      const deliveryDeadline = new Date();
      deliveryDeadline.setDate(deliveryDeadline.getDate() + deliveryDays);
      const transactionData = {
        title: formData.title.trim(),
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        deliveryDeadline: deliveryDeadline.toISOString(),
        description: formData.description.trim() || undefined,
        creatorRole: 'seller',
      };
      const response = await createTransaction(transactionData);
      const transaction = response?.data?.transaction || response?.data?.data?.transaction || response?.data || response;
      const transactionId = transaction?.id || transaction?.transaction?.id;
      if (!transactionId) {
        showError(language === 'fr' ? 'Transaction créée mais ID introuvable.' : 'Transaction created but ID not found.');
        return;
      }
      showSuccess(language === 'fr' ? 'Transaction créée avec succès!' : 'Transaction created successfully!');
      navigate(`/transactions/${transactionId}`);
    } catch (error) {
      const errorMessage = error?.message || error?.errors?.[0] || (language === 'fr' ? 'Échec de la création' : 'Failed to create transaction');
      showError(errorMessage);
    }
  };

  return (
    <div className="create-tx-form-wrap">
      {/* Progress */}
      <div className="dashboard-wt-card create-tx-progress-card">
        <div className="create-tx-progress-header">
          <span className="dashboard-wt-card-desc">
            {language === 'fr' ? 'Étape' : 'Step'} {step} {language === 'fr' ? 'sur' : 'of'} {totalSteps}
          </span>
          <span className="create-tx-progress-pct">{Math.round((step / totalSteps) * 100)}%</span>
        </div>
        <div className="create-tx-progress-bar">
          <div className="create-tx-progress-fill" style={{ width: `${(step / totalSteps) * 100}%` }} />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="create-tx-form">
        {step === 1 && (
          <div className="dashboard-wt-card create-tx-step-card">
            <div className="create-tx-step-head">
              <span className="create-tx-step-icon" aria-hidden>📦</span>
              <h2 className="dashboard-wt-card-title">
                {language === 'fr' ? 'Que vendez-vous?' : 'What are you selling?'}
              </h2>
              <p className="dashboard-wt-card-desc">
                {language === 'fr' ? 'Décrivez votre produit ou service' : 'Describe your product or service'}
              </p>
            </div>
            <Input
              label={language === 'fr' ? 'Titre de la transaction' : 'Transaction Title'}
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder={language === 'fr' ? 'Ex: Site web design' : 'Ex: Website Design'}
              error={errors.title}
              required
              className="create-tx-input"
            />
            <div className="create-tx-actions">
              <button
                type="button"
                className="dashboard-wt-btn-primary dashboard-wt-btn-large"
                onClick={handleNext}
                disabled={!formData.title.trim()}
              >
                {language === 'fr' ? 'Suivant' : 'Next'} →
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="dashboard-wt-card create-tx-step-card">
            <div className="create-tx-step-head">
              <span className="create-tx-step-icon" aria-hidden>💰</span>
              <h2 className="dashboard-wt-card-title">
                {language === 'fr' ? 'Montant et délai' : 'Amount & Delivery'}
              </h2>
              <p className="dashboard-wt-card-desc">
                {language === 'fr' ? 'Définissez le prix et le délai de livraison' : 'Set the price and delivery timeline'}
              </p>
            </div>
            <div className="create-tx-fields create-tx-grid-2">
              <Select
                label={language === 'fr' ? 'Devise' : 'Currency'}
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                options={[
                  { value: 'XAF', label: 'XAF (CFA Franc)' },
                  { value: 'KES', label: 'KES (Shilling)' },
                  { value: 'NGN', label: 'NGN (Naira)' },
                  { value: 'GHS', label: 'GHS (Cedi)' },
                  { value: 'ZAR', label: 'ZAR (Rand)' },
                ]}
                className="create-tx-select"
              />
              <Input
                label={language === 'fr' ? 'Montant' : 'Amount'}
                type="number"
                value={formData.amount}
                onChange={(e) => handleChange('amount', e.target.value)}
                placeholder="50000"
                error={errors.amount}
                required
                min="1"
                step="0.01"
                className="create-tx-input"
              />
            </div>
            <Input
              label={language === 'fr' ? 'Délai de livraison (jours)' : 'Delivery Time (days)'}
              type="number"
              value={formData.deliveryDays}
              onChange={(e) => handleChange('deliveryDays', e.target.value)}
              placeholder="14"
              error={errors.deliveryDays}
              required
              min="1"
              className="create-tx-input"
            />
            <div className="create-tx-actions create-tx-actions-2">
              <button type="button" className="dashboard-wt-btn-secondary dashboard-wt-btn-large" onClick={handleBack}>
                ← {language === 'fr' ? 'Retour' : 'Back'}
              </button>
              <button
                type="button"
                className="dashboard-wt-btn-primary dashboard-wt-btn-large"
                onClick={handleNext}
                disabled={!formData.amount || !formData.deliveryDays}
              >
                {language === 'fr' ? 'Suivant' : 'Next'} →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="dashboard-wt-card create-tx-step-card">
            <div className="create-tx-step-head">
              <span className="create-tx-step-icon" aria-hidden>✍️</span>
              <h2 className="dashboard-wt-card-title">
                {language === 'fr' ? 'Description (optionnel)' : 'Description (Optional)'}
              </h2>
              <p className="dashboard-wt-card-desc">
                {language === 'fr' ? 'Ajoutez plus de détails' : 'Add more details about your transaction'}
              </p>
            </div>
            <Textarea
              label={language === 'fr' ? 'Description' : 'Description'}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={language === 'fr' ? 'Ex: Site web de 5 pages...' : 'Ex: 5-page website with 3 revisions...'}
              rows={4}
              className="create-tx-textarea"
            />
            {amount > 0 && (
              <div className="dashboard-wt-card create-tx-fee-card">
                <div className="create-tx-fee-row">
                  <span className="dashboard-wt-card-desc">{language === 'fr' ? 'Montant:' : 'Amount:'}</span>
                  <span className="dashboard-wt-card-content">{formatCurrency(amount, formData.currency)}</span>
                </div>
                <div className="create-tx-fee-row">
                  <span className="dashboard-wt-card-desc">{language === 'fr' ? 'Frais (1.5%):' : 'Service fee (1.5%):'}</span>
                  <span className="dashboard-wt-card-content">{formatCurrency(fee, formData.currency)}</span>
                </div>
                <div className="create-tx-fee-row create-tx-fee-total">
                  <span className="dashboard-wt-card-title">{language === 'fr' ? "L'acheteur paie:" : 'Buyer pays:'}</span>
                  <span className="create-tx-fee-total-value">{formatCurrency(totalAmount, formData.currency)}</span>
                </div>
              </div>
            )}
            <div className="create-tx-actions create-tx-actions-2">
              <button type="button" className="dashboard-wt-btn-secondary dashboard-wt-btn-large" onClick={handleBack}>
                ← {language === 'fr' ? 'Retour' : 'Back'}
              </button>
              <button
                type="submit"
                className="dashboard-wt-btn-primary dashboard-wt-btn-large"
                disabled={loading}
              >
                {loading ? (language === 'fr' ? 'Création...' : 'Creating...') : (language === 'fr' ? 'Générer le lien' : 'Generate Link') + ' →'}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default CreateTransaction;
